import type { Climate, GameType } from '@ottd/api';

import { Hint } from '@/components/prototype/hint';
import { climateCaption, climateSrc } from '@/lib/climate';
import { serverTypeTheme } from '@/lib/server-type';
import { cn } from '@/lib/utils';

/*
 * The square CB/CV "type tile" - a dark chip with the two-letter mode label in
 * the type's accent color and its exact two-tone bevel border. Given a
 * climate, the chip is painted with that landscape's map (centred, cover)
 * under a dark scrim (server cards: the running game's map, or the last one
 * while idle).
 */
interface TypeTileProps {
  type: GameType | null | undefined;
  size?: number;
  /** Landscape painted behind the label; omitted / null keeps the plain chip. */
  climate?: Climate | null;
  className?: string;
}

/** The chip colour (#14161a) at 55% - the map stays visible under the label. */
const CLIMATE_SCRIM = 'rgba(20, 22, 26, 0.55)';

/** 1px black pixel outline: keeps the label legible on any map (snow, rivers). */
const CLIMATE_LABEL_OUTLINE = '1px 0 0 #000, -1px 0 0 #000, 0 1px 0 #000, 0 -1px 0 #000';

export function TypeTile({ type, size = 42, climate, className }: TypeTileProps) {
  const theme = serverTypeTheme(type);
  const hasClimate = climate !== null && climate !== undefined;
  const caption = hasClimate ? climateCaption(climate) : null;

  return (
    <Hint label={caption}>
      <span
        className={cn(
          'relative inline-flex shrink-0 items-center justify-center bg-[#14161a] bg-cover bg-center',
          className,
        )}
        style={{
          width: size,
          height: size,
          border: '2px solid',
          borderColor: theme.monoBorder,
          color: theme.monoColor,
          fontFamily: "'Silkscreen', sans-serif",
          fontSize: Math.round(size * 0.36),
          imageRendering: 'pixelated',
          backgroundImage: hasClimate ? `url(${climateSrc(climate)})` : undefined,
        }}
        aria-label={caption !== null ? `${theme.label}, ${caption}` : theme.label}
      >
        {hasClimate ? (
          <span className="absolute inset-0" style={{ background: CLIMATE_SCRIM }} aria-hidden />
        ) : null}
        <span
          className="relative"
          style={hasClimate ? { textShadow: CLIMATE_LABEL_OUTLINE } : undefined}
        >
          {theme.mono}
        </span>
      </span>
    </Hint>
  );
}
